import {
  BlockType,
  CHUNK_SIZE,
  getBlockIndex,
  isBlockSolid,
  getBreakSeconds,
  PLAYER_WIDTH,
  PLAYER_HEIGHT,
} from "@craftgaming/shared";
import type { RaycastHit } from "@craftgaming/shared";
import type { InputState } from "../core/input";

const UNBREAKABLE: Set<BlockType> = new Set([BlockType.BEDROCK, BlockType.WATER]);

export type BlockInteractionCallbacks = {
  getSelectedBlock: () => BlockType;
  /** Whatever is in the hand — tools change break speed (see tools.ts). */
  getHeldItem: () => number;
  getChunkBlocks: (cx: number, cy: number, cz: number) => Uint16Array | undefined;
  updateChunkBlock: (wx: number, wy: number, wz: number, blockId: BlockType) => void;
  getPlayerPosition: () => { x: number; y: number; z: number };
  /** Creative mode: near-instant breaking. */
  isCreative?: () => boolean;
};

export type BreakProgress = {
  x: number;
  y: number;
  z: number;
  progress: number; // 0-1
};

export type BlockInteraction = {
  /** suppressInteract: the game consumed right-click (eat/use block). */
  update: (hit: RaycastHit | null, input: InputState, dt: number, suppressInteract?: boolean) => void;
  getBreakProgress: () => BreakProgress | null;
  dispose: () => void;
};

export function createBlockInteraction(
  callbacks: BlockInteractionCallbacks
): BlockInteraction {
  let interactWasDown = false;

  // Breaking state
  let breakingBlock: { x: number; y: number; z: number; blockId: BlockType } | null = null;
  let breakProgress = 0; // 0-1
  let breakTime = 0;

  // Break progress bar UI
  const progressBar = document.createElement("div");
  progressBar.id = "break-progress";
  progressBar.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, 12px);
    width: 60px;
    height: 4px;
    background: rgba(0,0,0,0.5);
    border-radius: 2px;
    overflow: hidden;
    pointer-events: none;
    z-index: 15;
    display: none;
  `;

  const progressFill = document.createElement("div");
  progressFill.style.cssText = `
    height: 100%;
    width: 0%;
    background: #fff;
    border-radius: 2px;
    transition: width 0.05s linear;
  `;
  progressBar.appendChild(progressFill);
  document.body.appendChild(progressBar);

  function update(hit: RaycastHit | null, input: InputState, dt: number, suppressInteract = false) {
    const interactPressed = input.interact && !interactWasDown && !suppressInteract;
    interactWasDown = input.interact;

    // ─── Block breaking (hold left click) ───
    if (input.attack && hit) {
      const targetBlock = getBlockAtWorld(hit.blockX, hit.blockY, hit.blockZ);

      if (targetBlock !== undefined && !UNBREAKABLE.has(targetBlock)) {
        // Check if same block as last frame
        if (
          breakingBlock &&
          breakingBlock.x === hit.blockX &&
          breakingBlock.y === hit.blockY &&
          breakingBlock.z === hit.blockZ
        ) {
          // Continue breaking
          breakProgress += dt / breakTime;

          if (breakProgress >= 1) {
            // Block broken!
            callbacks.updateChunkBlock(hit.blockX, hit.blockY, hit.blockZ, BlockType.AIR);
            resetBreaking();
          } else {
            showProgress(breakProgress);
          }
        } else {
          // Start breaking new block — Minecraft rules: right tool is
          // fast, punching stone takes forever (see shared/tools.ts)
          breakingBlock = { x: hit.blockX, y: hit.blockY, z: hit.blockZ, blockId: targetBlock };
          breakTime = callbacks.isCreative?.()
            ? 0.08
            : getBreakSeconds(targetBlock, callbacks.getHeldItem());
          breakProgress = 0;
          showProgress(0);
        }
      } else {
        resetBreaking();
      }
    } else {
      // Not holding attack or no hit → cancel
      resetBreaking();
    }

    // ─── Block placing (right click, leading edge) ───
    if (interactPressed && hit) {
      const placeX = hit.adjacentX;
      const placeY = hit.adjacentY;
      const placeZ = hit.adjacentZ;

      if (wouldCollideWithPlayer(placeX, placeY, placeZ)) return;

      const existing = getBlockAtWorld(placeX, placeY, placeZ);
      if (existing !== undefined && isBlockSolid(existing)) return;

      const selectedBlock = callbacks.getSelectedBlock();
      if (selectedBlock === BlockType.AIR) return;

      callbacks.updateChunkBlock(placeX, placeY, placeZ, selectedBlock);
    }
  }

  function showProgress(progress: number) {
    progressBar.style.display = "block";
    progressFill.style.width = `${Math.min(progress * 100, 100)}%`;
  }

  function resetBreaking() {
    breakingBlock = null;
    breakProgress = 0;
    breakTime = 0;
    progressBar.style.display = "none";
    progressFill.style.width = "0%";
  }

  function getBreakProgress(): BreakProgress | null {
    if (!breakingBlock || breakProgress <= 0) return null;
    return {
      x: breakingBlock.x,
      y: breakingBlock.y,
      z: breakingBlock.z,
      progress: breakProgress,
    };
  }

  function getBlockAtWorld(wx: number, wy: number, wz: number): BlockType | undefined {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cy = Math.floor(wy / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const blocks = callbacks.getChunkBlocks(cx, cy, cz);
    if (!blocks) return undefined;

    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    return blocks[getBlockIndex(lx, ly, lz)] as BlockType;
  }

  function wouldCollideWithPlayer(bx: number, by: number, bz: number): boolean {
    const pos = callbacks.getPlayerPosition();
    const halfW = PLAYER_WIDTH / 2;

    const pMinX = pos.x - halfW;
    const pMaxX = pos.x + halfW;
    const pMinY = pos.y;
    const pMaxY = pos.y + PLAYER_HEIGHT;
    const pMinZ = pos.z - halfW;
    const pMaxZ = pos.z + halfW;

    return (
      pMinX < bx + 1 && pMaxX > bx &&
      pMinY < by + 1 && pMaxY > by &&
      pMinZ < bz + 1 && pMaxZ > bz
    );
  }

  function dispose() {
    progressBar.remove();
  }

  return { update, getBreakProgress, dispose };
}
